<template>
  <div class="rpilot-header d-flex align-center px-3 py-2 ga-2">
    <slot name="brand">
      <img
        :src="brandIcon"
        alt="RPilot"
        class="rpilot-brand-icon"
        @mouseenter="brandHovered = true"
        @mouseleave="brandHovered = false"
      />
      <span class="text-body-1 font-weight-medium ml-1">RPilot</span>
    </slot>

    <v-spacer />

    <slot name="header-actions" />

    <v-btn
      v-if="expandable"
      :icon="expanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"
      size="small"
      variant="text"
      :aria-label="expanded ? t('header.collapse') : t('header.expand')"
      :title="expanded ? t('header.collapse') : t('header.expand')"
      @click="$emit('toggle-expanded')"
    />

    <!-- Single-action button: opens the sidebar (Sessions / Skills / Settings
         tabs live inside the drawer). The dropdown-menu variant is gone — all
         per-widget controls now live in the drawer's Settings tab. -->
    <v-btn
      icon="mdi-dots-vertical"
      size="small"
      variant="text"
      :aria-label="t('header.menu')"
      @click="$emit('open-drawer', defaultDrawerTab)"
    />
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { computed, ref } from 'vue'
import { useTheme } from '../../composables/useTheme.js'
import iconDark from '../../assets/icons/rpilot-icon-dark.svg'
import iconLight from '../../assets/icons/rpilot-icon-light.svg'
import iconDarkSimple from '../../assets/icons/rpilot-dark-simple-animation.svg'
import iconLightSimple from '../../assets/icons/rpilot-light-simple-animation.svg'

const props = defineProps({
  showSessions: { type: Boolean, default: false },
  showSkills: { type: Boolean, default: false },
  expandable: { type: Boolean, default: false },
  expanded: { type: Boolean, default: false },
})

defineEmits(['toggle-expanded', 'open-drawer'])

const { isDark } = useTheme()

const brandHovered = ref(false)
const brandIcon = computed(() => {
  if (brandHovered.value) return isDark() ? iconDarkSimple : iconLightSimple
  return isDark() ? iconDark : iconLight
})

// Sessions is the user-facing landing tab; if the host disabled it, fall
// back to the next available tab so the drawer opens to something useful.
const defaultDrawerTab = computed(() => {
  if (props.showSessions) return 'sessions'
  if (props.showSkills) return 'skills'
  return 'settings'
})
</script>

<style scoped>
.rpilot-header {
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  flex-shrink: 0;
}
.rpilot-brand-icon {
  width: 36px;
  height: 36px;
  display: block;
}
</style>
